import { useState, useEffect, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signUp, fetchInvitedUserEmail } from '@/api/auth';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import {
  isValidEmail,
  saveConfirmFormData,
  saveUserSub,
  getInvitationIdFromSearch,
} from './utils';

export default function SignupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotification();

  // Invitation prefill
  const [invitationId, setInvitationId] = useState('');
  const [invitationLoading, setInvitationLoading] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [emailPrefilled, setEmailPrefilled] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyPrefilled, setCompanyPrefilled] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  // Submit state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Check for invitation ID on mount
  useEffect(() => {
    const id = getInvitationIdFromSearch(location.search);
    if (id) {
      setInvitationId(id);
      setInvitationLoading(true);
      fetchInvitedUserEmail(id)
        .then((data) => {
          setEmail(data.email.S);
          setConfirmEmail(data.email.S);
          setEmailPrefilled(true);
          setCompanyName(data.groupTitle.S);
          setCompanyPrefilled(true);
        })
        .catch(() => {
          // Invitation lookup failed — user can still sign up manually
        })
        .finally(() => setInvitationLoading(false));
    }
  }, [location.search]);

  function isFormComplete(): boolean {
    return (
      firstName.length > 0 &&
      lastName.length > 0 &&
      companyName.length > 0 &&
      email.length > 0 &&
      (emailPrefilled || confirmEmail.length > 0) &&
      password.length > 0 &&
      confirmPassword.length > 0
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!isFormComplete()) {
      notify({ id: String(Date.now()), type: 'info', message: 'Form is not complete' });
      return;
    }
    if (!termsAgreed || !privacyAgreed) {
      notify({
        id: String(Date.now()),
        type: 'info',
        message: 'Terms of service and privacy statement must be accepted to use Yemma',
      });
      return;
    }
    if (password !== confirmPassword) {
      notify({ id: String(Date.now()), type: 'info', message: 'Passwords do not match' });
      return;
    }
    if (!emailPrefilled && email.toLowerCase() !== confirmEmail.toLowerCase()) {
      notify({ id: String(Date.now()), type: 'info', message: 'Emails do not match' });
      return;
    }
    if (!isValidEmail(email)) {
      notify({ id: String(Date.now()), type: 'info', message: 'Given text is not an email address' });
      return;
    }

    setLoading(true);
    setError(false);
    try {
      const result = await signUp({ email, password, firstName, lastName });
      saveUserSub(email, result.userSub);
      saveConfirmFormData({
        status: 'initial',
        email: email.toLowerCase(),
        password,
        companyName,
        userSub: result.userSub,
        firstName,
        lastName,
        invitationId,
      });
      notify({
        id: String(Date.now()),
        type: 'success',
        message: `Code sent to ${email.toLowerCase()}`,
      });
      navigate('/signup/confirm');
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not register user' });
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  if (invitationLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
        <h2 className="mb-6 text-xl font-semibold">Sign up</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-accent-foreground">
              Email
            </label>
            <Input
              id="email"
              type="text"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailPrefilled(false); }}
              disabled={emailPrefilled}
              autoFocus={!emailPrefilled}
            />
          </div>

          {!emailPrefilled && (
            <div>
              <label htmlFor="confirmEmail" className="mb-1 block text-sm font-medium text-accent-foreground">
                Confirm email
              </label>
              <Input
                id="confirmEmail"
                type="text"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
              />
            </div>
          )}

          <div>
            <label htmlFor="company" className="mb-1 block text-sm font-medium text-accent-foreground">
              Company
            </label>
            <Input
              id="company"
              type="text"
              value={companyName}
              onChange={(e) => { setCompanyName(e.target.value); setCompanyPrefilled(false); }}
              disabled={companyPrefilled}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="mb-1 block text-sm font-medium text-accent-foreground">
                First name
              </label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="mb-1 block text-sm font-medium text-accent-foreground">
                Surname
              </label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-accent-foreground">
              Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-accent-foreground">
              Confirm password
            </label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={() => setTermsAgreed(!termsAgreed)}
                className="rounded border-border"
              />
              I accept the{' '}
              <a
                href="https://theyemma.com/termsOfService/index.html"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Terms of Service
              </a>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={privacyAgreed}
                onChange={() => setPrivacyAgreed(!privacyAgreed)}
                className="rounded border-border"
              />
              I accept the{' '}
              <a
                href="https://theyemma.com/privacyStatement/index.html"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Privacy Statement
              </a>
            </label>
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading && <Spinner size="sm" />}
            {error ? 'Try again' : 'Register'}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-primary hover:underline">
            Already have an account? Login here!
          </Link>
        </div>
      </div>
    </div>
  );
}
